/**
 * WorkflowSettingsTab - admin toggles and the danger zone.
 *
 * Toggles `is_active` and `owns_resource_lifecycle`, exposes the raw
 * `metadata` JSON object (advanced users only), and hosts the
 * soft-delete CTA.
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Separator } from '@/shared/components/ui/separator'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  useDeleteWorkflow,
  useUpdateWorkflow,
  type WorkflowDefinitionAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'
import { useNavigate } from 'react-router'

import { JsonObjectInput } from './action-modal/json-object-input'

interface WorkflowSettingsTabProps {
  workflow: WorkflowDefinitionAdmin
}

export function WorkflowSettingsTab({ workflow }: WorkflowSettingsTabProps) {
  const update = useUpdateWorkflow()
  const remove = useDeleteWorkflow()
  const navigate = useNavigate()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const metadataJson = useMemo(
    () => JSON.stringify(workflow.metadata ?? {}, null, 2),
    [workflow.metadata],
  )

  async function toggle(
    field: 'is_active' | 'owns_resource_lifecycle',
    next: boolean,
  ) {
    try {
      await update.mutateAsync({ uuid: workflow.uuid, [field]: next })
      toast.success('Updated')
    } catch (err) {
      toastApiError(err, 'Failed to update workflow')
    }
  }

  async function commitMetadata(next: Record<string, unknown>) {
    try {
      await update.mutateAsync({ uuid: workflow.uuid, metadata: next })
      toast.success('Metadata saved')
    } catch (err) {
      toastApiError(err, 'Failed to save metadata')
    }
  }

  async function handleDelete() {
    try {
      await remove.mutateAsync(workflow.uuid)
      toast.success('Workflow deleted')
      navigate('/manage/workflows', { replace: true })
    } catch (err) {
      toastApiError(err, 'Failed to delete workflow')
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <ToggleRow
        label="Active"
        description="Inactive workflows stop evaluating new instances."
        checked={workflow.is_active}
        onChange={(next) => toggle('is_active', next)}
      />

      <ToggleRow
        label="Owns resource lifecycle"
        description={
          'When on, this workflow is the authority for the resource\u2019s status ' +
          'field. Only one workflow per resource type can own the lifecycle; ' +
          'the backend enforces this via a unique constraint.'
        }
        checked={workflow.owns_resource_lifecycle}
        onChange={(next) => toggle('owns_resource_lifecycle', next)}
      />

      <Separator />

      <div className="flex flex-col gap-2">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Metadata (JSON)
        </Label>
        <p className="text-[10px] text-[var(--ink-3)]">
          Free-form metadata associated with the workflow. Commonly used for
          <code className="font-mono"> seeded </code> flags or admin notes.
        </p>
        <JsonObjectInput
          key={metadataJson}
          value={metadataJson}
          onCommit={commitMetadata}
        />
      </div>

      <Separator />

      <div className="rounded-[6px] border-[0.5px] border-[var(--red)] bg-[var(--red-bg-subtle)] p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-[2px] text-[var(--red)]" />
          <div className="flex flex-1 flex-col gap-2">
            <div>
              <p className="text-[12px] font-medium text-[var(--ink)]">
                Delete this workflow
              </p>
              <p className="text-[11px] text-[var(--ink-3)]">
                Soft-deletes the workflow, its current version, states, and
                actions. In-flight instances referencing it will no longer
                transition.
              </p>
            </div>
            <div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                disabled={remove.isPending}
              >
                <Trash2 size={13} className="mr-1" />
                Delete workflow
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${workflow.label}?`}
        description="This soft-deletes the workflow and every state and action on its current version. Instances still running against this workflow will halt. This cannot be undone from the UI."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={async () => {
          await handleDelete()
          setConfirmDelete(false)
        }}
      />
    </div>
  )
}

interface ToggleRowProps {
  label: string
  description: string
  checked: boolean
  onChange: (next: boolean) => void
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={() => onChange(!checked)}
      className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-3 text-left hover:border-[var(--green-border)]"
    >
      <Switch checked={checked} decorative />
      <div className="flex flex-col">
        <span className="text-[12px] text-[var(--ink)]">{label}</span>
        <span className="mt-0.5 text-[11px] text-[var(--ink-3)]">
          {description}
        </span>
      </div>
    </Button>
  )
}
